import { AfterViewChecked, Component, EventEmitter, Inject, OnInit, Output, ViewChild, ViewEncapsulation } from "@angular/core";
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from "angular-l10n";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { RegistrationService } from "../../services/registration.service";
import { EGender } from "../../models/user.model";
import { BusyIconService } from "../../services/busy.service";
import { Subscription } from "rxjs";
import { EnumParser } from "../../helpers/enum.helper";
import { ELanguage } from "../../models/userSettings.model";
import { EUserType } from "../../models/userInfo.model";
import { CountryService } from "../../services/country.service";
import { AppConstants } from "../../app.constants";
import { ICountry } from "../../models/country.model";
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import {faCheck, faExclamation, faExclamationTriangle} from "@fortawesome/free-solid-svg-icons";

@Component({
  selector: "registration",
  templateUrl: "registration.template.html",
  styleUrls: ["registration.style.less"],
  encapsulation: ViewEncapsulation.None
})
export class RegistrationComponent implements OnInit {
  @Output() stepChange = new EventEmitter();
  showDisclaimer: boolean = false;
  registrationToken: string = "";
  public step: number = 1;
  public selectedMode: EUserType;
  public selectCompanyPlaceholder: string;
  public selectableCompanies: string[] = [];
  public registrationForm: FormGroup;
  public genderSelection: any[];
  public EUserType = EUserType;
  public allCountries: ICountry[] = [];

  public checkMark: IconProp = faCheck;
  public exclamationMark: IconProp = faExclamation;

  private allCompanies: { name: string, type: EUserType }[] = [];

  constructor(public router: Router,
              @Inject(L10N_LOCALE) public locale: L10nLocale,
              public translationService: L10nTranslationService,
              fb: FormBuilder,
              private countryService: CountryService,
              private registrationService: RegistrationService,
              private busyIconService: BusyIconService,
              private route: ActivatedRoute) {

    this.registrationForm = fb.group({
      company: [null, Validators.required],
      firstname: ["", Validators.compose([Validators.required, Validators.maxLength(64)])],
      lastname: ["", Validators.compose([Validators.required, Validators.maxLength(64)])],
      gender: [null, Validators.required],
      remark: ["", Validators.compose([Validators.required, Validators.maxLength(128)])],
      street: ["", Validators.compose([Validators.required, Validators.maxLength(128)])],
      zip: ["", Validators.compose([Validators.required, Validators.maxLength(10)])],
      city: ["", Validators.compose([Validators.required, Validators.maxLength(64)])],
      country: [null, Validators.required],
      one_of_two: fb.group({
        phone: ["", Validators.pattern(AppConstants.PHONE_VALIDATION_REGEX)],
        mobile: ["", Validators.pattern(AppConstants.PHONE_VALIDATION_REGEX)],
      }, {validator: this.onOfTwo}),
      equal_email: fb.group({
        email: ["", Validators.compose([Validators.required, Validators.maxLength(64), Validators.pattern(AppConstants.EMAIL_VALIDATION_REGEX)])],
        confirm_email: ["", Validators.compose([Validators.required, Validators.maxLength(64)])]
      }, {validator: this.areEqual}),
      equal_password: fb.group({
        password: ["", Validators.compose([Validators.required, Validators.pattern(AppConstants.PASSWORD_VALIDATION_REGEX)])],
        confirm_password: ["", Validators.compose([Validators.required, Validators.minLength(8)])]
      }, {validator: this.areEqual}),
      termsOfUse: [false, Validators.requiredTrue]
    });
  }

  onOfTwo(group: FormGroup) {
    for (const name in group.controls) {
      if (group.controls[name].value) {
        return null;
      }
    }
    return {
      oneOfTwo: true
    };
  }

  areEqual(group: FormGroup) {
    let val;
    let valid = true;

    for (const name in group.controls) {
      if (val === undefined) {
        val = group.controls[name].value;
      } else {
        if (val !== group.controls[name].value) {
          valid = false;
          break;
        }
      }
    }

    if (valid) {
      return null;
    }

    return {
      areEqual: true
    };
  }

  isStepActive(step: number) {
    return this.step === step;
  }

  changeSelectedMode(mode: EUserType, patch:boolean, init: boolean = false) {
    if (mode === EUserType.SUPPLIER) {
      mode = EUserType.CARRIER;
    }
    if (mode !== this.selectedMode || init) {
      this.selectedMode = mode;
      if(patch){
        this.registrationForm.patchValue({company: null});
      }
      if (mode === EUserType.CUSTOMER) {
        this.registrationForm.controls["remark"].disable();
      } else {
        this.registrationForm.controls["remark"].enable();
      }
      this.selectableCompanies = [];
      for (const company of this.allCompanies) {
        if (company.type === mode) {
          this.selectableCompanies.push(company.name);
        }
      }
    }
    this.saveModeToLocalStorage()
  }

  changeLanguage(language: string): void {
    this.translationService.setLocale({language});
  }

  syncCountries() {
    const selectedLanguage: ELanguage = ELanguage[this.locale.language.toUpperCase()];
    this.allCountries = this.countryService.getAllCountries(selectedLanguage);
  }

  ngOnInit() {
    this.syncCountries();
    const storedRegistrationForm = JSON.parse(localStorage.getItem("registration-form"))
    if(storedRegistrationForm != null){
      this.registrationForm.patchValue(storedRegistrationForm)
    }
    const storedStep = JSON.parse(localStorage.getItem("currentStep"));
    if(storedStep != null){
      this.step = storedStep;
    }
    const storedMode = JSON.parse(localStorage.getItem("currentMode"));
    if(storedMode != null){
      this.selectedMode = storedMode
    }
    this.translationService.onChange().subscribe(
      () => {
        const enumParser = new EnumParser(this.translationService);
        this.genderSelection = enumParser.getTranslatedNamesAndValues(EGender, "ENUMS.GENDER");
        this.selectCompanyPlaceholder = this.translationService.translate("REGISTRATION.COMPANY_PLACEHOLDER_NO_ROLE");
        this.syncCountries();
      }
    );

    const getAllCompaniesSubscription: Subscription = this.registrationService.getAllCompaniesWithoutTestCompanies().subscribe((companies) => {
        if (companies) {
          this.allCompanies = companies.filter((company) => company.type );
          this.allCompanies.sort( (a, b) => {
            if (a.name < b.name) { return -1; }
            if (a.name > b.name) { return 1; }
            return 0;
          });
          if (this.route.snapshot.queryParamMap.get("accountType")) {
            this.route.snapshot.queryParamMap.get("accountType") === "CARRIER" ? this.changeSelectedMode(EUserType.CARRIER,false) : this.changeSelectedMode(EUserType.CUSTOMER,false);
          }
          if (this.route.snapshot.queryParamMap.get("company")) {
            this.registrationForm.controls["company"].setValue(this.route.snapshot.queryParamMap.get("company"));
          }
          if (this.route.snapshot.queryParamMap.get("remark")) {
            this.registrationForm.controls["remark"].setValue(this.route.snapshot.queryParamMap.get("remark"));
          }
          if (this.route.snapshot.queryParamMap.get("email")) {
            this.registrationForm.get("equal_email").get("email").setValue(this.route.snapshot.queryParamMap.get("email"));
            this.registrationForm.get("equal_email").get("confirm_email").setValue(this.route.snapshot.queryParamMap.get("email"));
          }

          if (storedMode) {
            this.changeSelectedMode(storedMode, false, true);
          }
        }
      },
      (err) => {
        console.log(err);
      });
    this.busyIconService.showBusyIcon(getAllCompaniesSubscription);
  }

  registrationTokenValid(): boolean {
    return this.registrationToken.length === 6;
  }
  saveFormToLocalStorage(){
    localStorage.setItem("registration-form",JSON.stringify(this.registrationForm.value));
  }
  saveCurrentStepToLocalStorage(){
    localStorage.setItem("currentStep",JSON.stringify(this.step))
  }
  saveModeToLocalStorage(){
    localStorage.setItem("currentMode",JSON.stringify(this.selectedMode))
  }
}
